import Component from '@glimmer/component';
import { service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import t from 'ember-intl/helpers/t';
import AlertBanner from 'tio-ui/components/alert-banner';
import SectionPanel from 'tio-ui/components/section-panel';
import { PhListChecks } from 'ember-phosphor-icons';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';
import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';
import {
  answerProfileUpdates,
  REQUESTING_APP,
  type Sf0726NsldsPrefill,
} from 'tio-common/utils/strategy-finder-0726';
import StrategyFinder0726Form from 'tio-common/components/strategy-finder-0726/form';
import type AnswerProfileService from 'tio-employee/services/answer-profile';

interface S {
  Args: {
    model: {
      loanSummary: LoanSummary;
      user: UserModel;
      nsldsPrefill: Sf0726NsldsPrefill | null;
    };
  };
}

export default class RepaymentStrategyFinderQuestionnaireTemplate extends Component<S> {
  @service declare router: RouterService;
  @service declare answerProfile: AnswerProfileService;

  // Persist the entered answers to the shared answer profile (employee-only;
  // the agent app omits this callback).
  persistAnswers = (updates: ReturnType<typeof answerProfileUpdates>) =>
    this.answerProfile.storeAnswers(REQUESTING_APP, updates);

  get hasLoans() {
    return (this.args.model.loanSummary.summaryByLoanType || []).length > 0;
  }

  /**
   * Open the saved recommendation through the results route's loading hook.
   * @param recommendation Persisted result returned by the questionnaire.
   * @returns Nothing; starts a route transition.
   */
  didSave = (recommendation: RecommendationModel) => {
    this.router.transitionTo(
      'authenticated.repaying-student-debt.repayment-strategy-finder.results',
      recommendation.id
    );
  };

  <template>
    <SectionPanel
      @headingLevel={{2}}
      @icon={{PhListChecks}}
      @iconColor="violet"
      @title={{t "strategy_finder_0726.get_started_title"}}
    >
      <:body>
        {{#if this.hasLoans}}
          <StrategyFinder0726Form
            @user={{@model.user}}
            @loanSummary={{@model.loanSummary}}
            @nsldsPrefill={{@model.nsldsPrefill}}
            @didSave={{this.didSave}}
            @onSaveAnswers={{this.persistAnswers}}
            @nsldsUploadRoute="authenticated.observability.upload"
            class="md:max-w-3xl"
          />
        {{else}}
          <AlertBanner @intent="warning" data-test-sf-0726-no-loans>
            <p class="text-sm">{{t "strategy_finder_0726.no_loans"}}</p>
          </AlertBanner>
        {{/if}}
      </:body>
    </SectionPanel>
  </template>
}
